import { useEffect, useState, useSyncExternalStore } from 'react'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { cn } from '@/lib/cn'
import { noIndexedDbAvailable, outboxCountsSnapshot, subscribeOutboxCounts } from '@/outbox/db'
import type { OutboxEntry } from '@/outbox/db'
import { useClearanceStatus, usePersistenceOutcome } from '@/outbox/clearance'
import type { PersistenceOutcome } from '@/outbox/clearance'
import { pending, refused as refusedEntries } from '@/outbox/enqueue'
import { RefusedEntry, describeEntry } from '@/outbox/repair'
// Registers `toy.tally.bump`'s repair as a side effect of import — imported
// here (not left to whatever happens to import `outbox/repair` first) so
// the REAL shell always carries every repair this app ships, the same
// reason `tools/structural/repair-coverage.test.ts` imports it directly
// rather than assuming some other module graph already has.
import '@/outbox/repairs.registry'
import { StorageCleared } from './StorageCleared'

/**
 * The tray's detail-view text for `navigator.storage.persist()`'s outcome
 * (design §5.4). Four states, not two `'unknown'` folded into whichever
 * side felt closer: `'unknown'` means the request has not resolved yet
 * (still awaited, or `main.tsx` has not fired it in this test render) and
 * must not be shown as either "protected" or "not protected" while that is
 * genuinely undetermined; `'unavailable'` means the browser was never even
 * ASKED (no Storage Manager API), which is a materially different fact
 * from being asked and refused.
 */
function persistenceLine(outcome: PersistenceOutcome | 'unknown'): string {
  switch (outcome) {
    case 'granted':
      return "This device's storage is protected from automatic clean-up by the browser."
    case 'denied':
      return "This device's storage is NOT protected from automatic clean-up — the browser may clear it under storage pressure."
    case 'unavailable':
      return 'This browser cannot protect storage from automatic clean-up.'
    case 'unknown':
      return 'Checking whether this device can protect its storage…'
  }
}

/**
 * One durable surface for queued/refused/lost work (design decision named
 * in the task brief: "one durable surface in the chrome, and the repair
 * happens in the tray"). Read-only reactivity, no polling: `subscribeOutboxCounts`
 * (`outbox/db.ts`) notifies on every `enqueue()`/`drain()`/`discard()`
 * mutation, and this hook re-fetches the full row lists whenever those
 * counts change — so the badge and the open tray both stay in step with
 * Dexie without this component ever guessing when to re-check.
 */
function useOutboxEntries(): {
  readonly counts: { readonly queued: number; readonly refused: number }
  readonly queued: readonly OutboxEntry[]
  readonly refused: readonly OutboxEntry[]
} {
  const counts = useSyncExternalStore(subscribeOutboxCounts, outboxCountsSnapshot)
  const [entries, setEntries] = useState<{ queued: OutboxEntry[]; refused: OutboxEntry[] }>({
    queued: [],
    refused: [],
  })

  useEffect(() => {
    // `noIndexedDbAvailable()` (`db.ts`) — the SAME guard `outboxIsEmpty()`
    // uses, for the SAME reason, measured again here: this component now
    // mounts inside every `AppShell`, including every app-integration test
    // that has never installed `fake-indexeddb` (nearly all of them, per
    // `db.ts`'s own `outboxDb` comment). Skipping the read entirely when
    // there is structurally no possible backing store — rather than calling
    // `pending()`/`refused()` and letting them reject — is what keeps this
    // effect from throwing an unhandled `MissingAPIError` in every one of
    // those tests; a real browser always has `indexedDB`, so this branch is
    // inert in production.
    if (noIndexedDbAvailable()) return
    let cancelled = false
    void Promise.all([pending(), refusedEntries()]).then(([queued, refused]) => {
      if (!cancelled) setEntries({ queued, refused })
    })
    return () => {
      cancelled = true
    }
    // Depends on the WHOLE `counts` object, by reference — not
    // `[counts.queued, counts.refused]`, which this effect used before
    // web-pre-deploy items 1/2. That version assumed the two counts were
    // the ONLY thing that could make the underlying rows differ from what
    // is already in state, which stopped being true the moment a resend
    // could be refused AGAIN for a DIFFERENT reason: the row's `refusals`
    // content changes while `counts.refused` stays the same (it was already
    // `refused`, still is), so depending on the primitive numbers alone
    // missed exactly that refetch and left `RefusedEntry` showing a stale
    // reason.  `db.ts`'s `createStore` guarantees the snapshot reference is
    // stable BETWEEN `set` calls and fresh on every one — so depending on
    // `counts` itself re-runs this effect on every genuine
    // `notifyOutboxChanged()` (`enqueue`/`drain`/`discard`, and now a
    // resend's own content-only republish), never merely on every render,
    // which is the property that made the OLD, narrower dependency look
    // like a safe optimisation in the first place. No
    // `eslint-plugin-react-hooks` is installed in this repo (`AppShell.tsx`'s
    // own `queryHook` comment notes the same thing), so there is no
    // exhaustive-deps rule to disable here — this comment is the only
    // record of the choice.
  }, [counts])

  return { counts, ...entries }
}

/**
 * The nav's own Sync entry (brief step 2). Opened "from a nav badge present
 * in both the sidebar and the bottom bar" — which, in THIS app, is one and
 * the same element: `AppShell.tsx`'s own top comment establishes ONE
 * responsive `<nav>` that becomes a sidebar or a bottom bar by breakpoint,
 * never two parallel trees, and this component is rendered as one more item
 * inside that SAME nav rather than a second copy elsewhere — so "present in
 * both" falls out of `AppShell`'s existing design instead of needing its
 * own duplication here.
 *
 * Three sections inside the sheet — Waiting to send, Needs your attention,
 * Lost — "each rendering nothing when empty rather than an empty heading"
 * (brief step 2). "Lost" (Task 19: `./StorageCleared.tsx`) owns its own
 * VISIBILITY decision (`status !== 'cleared'` renders nothing) — the same
 * "nothing when empty" rule its two siblings below already follow — but,
 * fixed in review round 1's Important 1, no longer owns WHEN the underlying
 * detection runs: `useClearanceStatus()` is called HERE, in this component's
 * own body, not only inside `StorageCleared` — see that call's own comment
 * for why the difference matters.
 */
export function SyncTray() {
  const { counts, queued, refused } = useOutboxEntries()
  const persistence = usePersistenceOutcome()
  // CORRECTED, review round 1's Important 1: this hook used to be called
  // only from INSIDE `StorageCleared`, which is rendered inside
  // `<SheetContent>` — a Radix portal that mounts nothing in its children
  // while the sheet is closed (no `forceMount`). That meant detection never
  // ran until a site manager happened to open the tray for some OTHER
  // reason, and the badge below never lit up either (it read only
  // `counts.queued + counts.refused`, which is 0 after a clear — nothing
  // queued or refused, just everything gone). Called HERE instead, in
  // `SyncTray`'s own body — which renders whenever the shell does,
  // regardless of whether the sheet is open — the one-time check
  // (`beginClearanceCheck`, guarded, idempotent) starts as soon as the shell
  // mounts, and the value is threaded down to `StorageCleared` as a prop
  // rather than that component subscribing a SECOND time.
  const clearanceStatus = useClearanceStatus()
  // The BADGE reads the synchronous `counts` mirror, not `queued.length +
  // refused.length` — deliberately, and MEASURED, not a style preference:
  // `counts` is already correct the instant `enqueue()`/`drain()` mutate it
  // (`db.ts`'s `noteRowAdded`/`noteRefusedAdded` etc. update it
  // synchronously), where the full row LISTS only catch up once this
  // component's own effect above has finished its `pending()`/`refused()`
  // round trip. A badge that waited for the slower of the two would show
  // nothing for a beat after every queue/drain — precisely the kind of gap
  // "a phone in a pocket has no signal at all" (this behaviour's own
  // mutation) exists to close.
  //
  // `+ (clearanceStatus === 'cleared' ? 1 : 0)` — review round 1's Important
  // 1, second half: a genuine clear leaves `counts.queued`/`counts.refused`
  // both at 0 (there is nothing left to queue or refuse — the outbox itself
  // was wiped), so without this the badge stayed invisible for the single
  // worst outcome this whole tray exists to surface. `'cleared'` contributes
  // exactly one, the same weight as a single queued or refused row, rather
  // than a count of anything real (there is no real count to give it — see
  // `clearance.ts`'s own top comment on why one can never be invented).
  const total = counts.queued + counts.refused + (clearanceStatus === 'cleared' ? 1 : 0)

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          className={cn(
            // `min-w-0`, same fix and same measured bug as AppShell's
            // NavLink items — this entry shares the bar and must shrink
            // with its siblings rather than hold a min-content floor.
            'relative flex min-w-0 flex-1 flex-col items-center gap-1 px-2 py-[var(--gf-row-y)] text-center',
            'text-[length:var(--gf-text)] text-muted-foreground hover:text-foreground',
            'min-h-[var(--gf-target)] md:min-h-0 md:flex-none md:flex-row md:justify-start md:gap-2 md:rounded-md md:px-3 md:hover:bg-accent',
          )}
        >
          <span>Sync</span>
          {total > 0 && (
            <Badge variant="secondary" aria-label={`${total} item${total === 1 ? '' : 's'} in the sync tray`}>
              {total}
            </Badge>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Sync</SheetTitle>
          <SheetDescription>Work waiting to send, and anything that needs your attention.</SheetDescription>
        </SheetHeader>
        <div className="space-y-6 px-4 pb-4">
          {queued.length > 0 && (
            <section aria-label="Waiting to send" className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">Waiting to send</h3>
              <ul className="space-y-1">
                {queued.map((entry) => (
                  <li key={entry.idempotencyKey} className="text-[length:var(--gf-text)]">
                    {describeEntry(entry)}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {refused.length > 0 && (
            <section aria-label="Needs your attention" className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">Needs your attention</h3>
              <div>
                {refused.map((entry) => (
                  <RefusedEntry key={entry.idempotencyKey} entry={entry} />
                ))}
              </div>
            </section>
          )}

          <StorageCleared status={clearanceStatus} />
        </div>
        {/*
          The tray's "detail view" design §5.4 asks `navigator.storage.
          persist()`'s outcome to be surfaced in — a denied request is a
          materially weaker durability posture, and the site manager relying
          on it deserves to be able to find that out. Rendered unconditionally
          (not gated on any section above having content): this is a property
          of the DEVICE, not of what happens to be queued right now, so it
          stays visible every time the tray opens rather than only alongside
          other bad news.
        */}
        <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">{persistenceLine(persistence)}</div>
      </SheetContent>
    </Sheet>
  )
}
